import { useEffect, useMemo, useState } from 'react';
import type { CrmOpportunity, CrmPipeline, CrmStage, Rol } from '@matias-asset-os/contracts';
import { formatearImporteDesdeCents, t, type Idioma } from '@matias-asset-os/i18n';
import { api } from '../api.js';
import { AsistenteCrm } from '../componentes/AsistenteCrm.js';
import { FichaOportunidad } from '../componentes/FichaOportunidad.js';
import { Icono, type NombreIcono } from '../componentes/Iconos.js';
import { Obras } from '../obras/Obras.jsx';
import { Puertas } from '../puertas/Puertas.js';

interface Props {
  idioma: Idioma;
  rol: Rol;
  nombreUsuario: string;
  onVolver: () => void;
  /** Abrir esta oportunidad al entrar: viene de una tarea del panel del día. */
  abrirOportunidadId?: string;
}

/**
 * EL CRM SON TRES HERRAMIENTAS, NO UNA CON PESTANAS.
 *
 * Los embudos mueven oportunidades entre etapas. Las obras viven en un mapa y
 * se siguen durante meses. El puerta a puerta se rellena de pie en una acera.
 * Comparten la palabra «comercial» y nada mas: ni los datos, ni el ritmo, ni
 * quien las usa.
 *
 * Por eso al entrar se elige, en vez de caer en una de las tres y descubrir las
 * otras por casualidad. Y por eso un captador —que solo hace calle— entra
 * directo a lo suyo sin ver una puerta que no puede abrir.
 */
type Seccion = 'inicio' | 'embudos' | 'obras' | 'puertas';

/**
 * CRM — tablero de embudos.
 *
 * Los cuatro embudos vienen del backend (se instalan solos por empresa). Cada
 * columna es una etapa; cada tarjeta, una oportunidad. Mover una tarjeta a una
 * etapa ganadora o perdida la cierra: la regla vive en el servidor, no aquí.
 */
export function Crm({ idioma, rol, nombreUsuario, onVolver, abrirOportunidadId }: Props) {
  /*
    Un captador no tiene embudos: su CRM son las obras y las puertas. Empieza en
    el indice igual que el resto, pero con dos tarjetas en vez de tres.
  */
  const esCaptador = rol === 'CAPTADOR';
  const [seccion, setSeccion] = useState<Seccion>('inicio');
  const [embudos, setEmbudos] = useState<CrmPipeline[] | null>(null);
  const [embudoId, setEmbudoId] = useState<string | null>(null);
  const [oportunidades, setOportunidades] = useState<CrmOpportunity[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [nuevoNombre, setNuevoNombre] = useState('');
  const [valorEuros, setValorEuros] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [asistenteAbierto, setAsistenteAbierto] = useState(false);
  /** La oportunidad abierta en detalle, si hay alguna. */
  const [detalle, setDetalle] = useState<CrmOpportunity | null>(null);

  /*
    Lo que vuelve cambiado —del asistente o del detalle— sustituye a lo que
    habia, sin recargar el tablero: recargarlo devolveria el scroll de las
    columnas al principio cada vez que se anota algo.
  */
  function sustituir(cambiadas: CrmOpportunity[]) {
    setOportunidades((prev) => {
      const actualizadas = prev
        .map((o) => cambiadas.find((c) => c.id === o.id) ?? o)
        // La que se ha llevado a otro embudo deja de estar en este tablero.
        .filter((o) => o.pipelineId === embudoId);
      // Y la recién creada en este embudo aparece, arriba de su columna.
      const nuevas = cambiadas.filter(
        (c) => c.pipelineId === embudoId && !prev.some((o) => o.id === c.id),
      );
      return [...nuevas, ...actualizadas];
    });
    setDetalle((d) => (d ? (cambiadas.find((c) => c.id === d.id) ?? d) : d));
  }

  /*
    DESDE UNA TAREA DEL PANEL, DIRECTO A SU FICHA. Quien pulsa «Llamar a
    Christian» en su día quiere ver a Christian, no el índice del CRM para
    buscarlo: se abre su embudo y su ficha.
  */
  useEffect(() => {
    if (!abrirOportunidadId) return;
    api
      .crmOportunidad(abrirOportunidadId)
      .then(({ oportunidad }) => {
        setSeccion('embudos');
        setEmbudoId(oportunidad.pipelineId);
        setDetalle(oportunidad);
      })
      .catch(() => setError(t(idioma, 'crm.noSeCargaronOportunidades')));
  }, [abrirOportunidadId, idioma]);

  const embudo = useMemo(
    () => embudos?.find((e) => e.id === embudoId) ?? null,
    [embudos, embudoId],
  );

  useEffect(() => {
    api
      .crmEmbudos()
      .then(({ embudos: lista }) => {
        setEmbudos(lista);
        setEmbudoId((actual) => actual ?? lista[0]?.id ?? null);
      })
      .catch(() => setError(t(idioma, 'crm.noSeCargaronEmbudos')));
  }, [idioma]);

  useEffect(() => {
    if (!embudoId) return;
    setOportunidades([]);
    api
      .crmOportunidades(embudoId)
      .then(({ oportunidades: lista }) => setOportunidades(lista))
      .catch(() => setError(t(idioma, 'crm.noSeCargaronOportunidades')));
  }, [embudoId, idioma]);

  /*
    «AÑADIR» NO HACIA NADA Y NO DECIA POR QUE.

    Con el nombre vacio la funcion se volvia por donde habia venido, en
    silencio: se pulsaba el boton y no pasaba nada, ni un aviso. Parecia
    estropeado. Ahora el boton esta apagado mientras no haya un nombre —se ve
    que no se puede pulsar— y si aun asi se llama, lo dice.
  */
  async function crear() {
    if (!embudo) return;
    if (nuevoNombre.trim().length < 2) {
      setError(t(idioma, 'crm.faltaNombre'));
      return;
    }
    setGuardando(true);
    setError(null);
    try {
      const euros = Number(valorEuros.replace(',', '.'));
      const { oportunidad } = await api.crmCrearOportunidad({
        pipelineId: embudo.id,
        nombre: nuevoNombre.trim(),
        ...(Number.isFinite(euros) && euros > 0 ? { valorCents: Math.round(euros * 100) } : {}),
      });
      setOportunidades((prev) => [oportunidad, ...prev]);
      setNuevoNombre('');
      setValorEuros('');
    } catch {
      setError(t(idioma, 'crm.noSeCreo'));
    } finally {
      setGuardando(false);
    }
  }

  async function mover(op: CrmOpportunity, stageId: string) {
    if (stageId === op.stageId) return;
    try {
      const { oportunidad } = await api.crmMoverOportunidad(op.id, stageId);
      setOportunidades((prev) => prev.map((o) => (o.id === op.id ? oportunidad : o)));
    } catch {
      setError(t(idioma, 'crm.noSeMovio'));
    }
  }

  const porEtapa = (stage: CrmStage) => oportunidades.filter((o) => o.stageId === stage.id);
  const valorEtapa = (stage: CrmStage) =>
    porEtapa(stage).reduce((sum, o) => sum + (o.valorCents ?? 0), 0);

  if (seccion === 'obras') {
    return (
      <Obras
        idioma={idioma}
        rol={rol}
        nombreUsuario={nombreUsuario}
        onVolver={() => setSeccion('inicio')}
      />
    );
  }

  if (seccion === 'puertas') {
    return <Puertas idioma={idioma} rol={rol} onVolver={() => setSeccion('inicio')} />;
  }

  if (seccion === 'inicio') {
    const accesos: Array<{ clave: Seccion; icono: NombreIcono; titulo: string; pie: string }> = [
      ...(esCaptador
        ? []
        : [
            {
              clave: 'embudos' as Seccion,
              icono: 'embudo' as NombreIcono,
              titulo: 'crm.pipelines',
              pie: 'crm.pipelinesPie',
            },
          ]),
      { clave: 'obras', icono: 'edificio', titulo: 'crm.obras', pie: 'crm.obrasPie' },
      { clave: 'puertas', icono: 'pin', titulo: 'pap.titulo', pie: 'pap.sub' },
    ];
    return (
      <main>
        <div className="cabecera-seccion">
          <div>
            <h1>CRM</h1>
            <p>{t(idioma, 'crm.sub')}</p>
          </div>
          <div className="acciones">
            <button type="button" className="secundario" onClick={onVolver}>
              {t(idioma, 'app.volver')}
            </button>
          </div>
        </div>
        <div className="crm-accesos">
          {accesos.map((a) => (
            <button
              key={a.clave}
              type="button"
              className="tarjeta crm-acceso"
              onClick={() => setSeccion(a.clave)}
            >
              <Icono nombre={a.icono} />
              <b>{t(idioma, a.titulo)}</b>
              <small className="suave">{t(idioma, a.pie)}</small>
            </button>
          ))}
        </div>
      </main>
    );
  }

  return (
    <main>
      <div className="cabecera-seccion">
        <div>
          <h1>CRM</h1>
          <p>{t(idioma, 'crm.sub')}</p>
        </div>
        <div className="acciones">
          {/* Volver al indice, que es donde estan las otras dos herramientas. */}
          <button type="button" className="secundario" onClick={() => setSeccion('inicio')}>
            <Icono nombre="volver" /> {t(idioma, 'app.volver')}
          </button>
          <button type="button" onClick={() => setAsistenteAbierto((a) => !a)}>
            <Icono nombre="chispa" /> {t(idioma, 'crm.asistente.abrir')}
          </button>
        </div>
      </div>

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}

      {embudos === null && !error && <p>{t(idioma, 'app.cargando')}</p>}

      {embudos !== null && (
        <>
          <div className="crm-embudos">
            {embudos.map((e) => (
              <button
                key={e.id}
                type="button"
                className={e.id === embudoId ? '' : 'secundario'}
                onClick={() => setEmbudoId(e.id)}
              >
                {e.nombre}
              </button>
            ))}
          </div>

          {embudo && (
            <div className="tarjeta crm-alta">
              <div className="campo">
                <label htmlFor="op-nombre">{t(idioma, 'crm.nuevaOportunidad')}</label>
                <input
                  id="op-nombre"
                  value={nuevoNombre}
                  placeholder={t(idioma, 'crm.nombrePlaceholder')}
                  onChange={(ev) => setNuevoNombre(ev.target.value)}
                />
              </div>
              <div className="campo">
                <label htmlFor="op-valor">{t(idioma, 'crm.valor')}</label>
                <input
                  id="op-valor"
                  value={valorEuros}
                  inputMode="decimal"
                  placeholder="0"
                  onChange={(ev) => setValorEuros(ev.target.value)}
                />
              </div>
              <button
                type="button"
                onClick={() => void crear()}
                disabled={guardando || nuevoNombre.trim().length < 2}
              >
                {t(idioma, 'crm.anadir')}
              </button>
            </div>
          )}

          {embudo && (
            <div className="crm-tablero tabla-scroll">
              {embudo.etapas.map((stage) => (
                <div key={stage.id} className="crm-columna">
                  <header>
                    <span className="crm-etapa-nombre">{stage.nombre}</span>
                    <span className="crm-etapa-meta">
                      {porEtapa(stage).length} · {Math.round(stage.probabilidad)}%
                    </span>
                  </header>
                  {valorEtapa(stage) > 0 && (
                    <p className="crm-etapa-valor">
                      {formatearImporteDesdeCents(valorEtapa(stage), idioma)}
                    </p>
                  )}
                  <div className="crm-tarjetas">
                    {porEtapa(stage).map((op) => (
                      <article
                        key={op.id}
                        className="crm-op crm-op-pulsable"
                        role="button"
                        tabIndex={0}
                        aria-label={t(idioma, 'crm.detalle.abrir', { nombre: op.nombre })}
                        onClick={() => setDetalle(op)}
                        onKeyDown={(ev) => {
                          if (ev.target !== ev.currentTarget) return;
                          if (ev.key === 'Enter' || ev.key === ' ') {
                            ev.preventDefault();
                            setDetalle(op);
                          }
                        }}
                      >
                        <p className="crm-op-nombre">{op.nombre}</p>
                        {op.exclusivoNombre && (
                          <span className="pastilla exclusiva">
                            <Icono nombre="prohibido" />{' '}
                            {t(idioma, 'crm.exclusiva', { nombre: op.exclusivoNombre })}
                          </span>
                        )}
                        <p className="crm-op-cod">{op.codigo}</p>
                        {op.valorCents != null && (
                          <p className="crm-op-valor">
                            {formatearImporteDesdeCents(op.valorCents, idioma, {
                              moneda: op.moneda,
                            })}
                          </p>
                        )}
                        <Contacto op={op} idioma={idioma} />
                        {op.estado !== 'ABIERTA' && (
                          <span
                            className={`pastilla ${op.estado === 'GANADA' ? 'VERDE' : 'ROJO'}`}
                          >
                            {t(idioma, op.estado === 'GANADA' ? 'crm.ganada' : 'crm.perdida')}
                          </span>
                        )}
                        <select
                          aria-label={t(idioma, 'crm.mover', { nombre: op.nombre })}
                          value={op.stageId}
                          // Elegir columna no abre la ficha.
                          onClick={(ev) => ev.stopPropagation()}
                          onKeyDown={(ev) => ev.stopPropagation()}
                          onChange={(ev) => void mover(op, ev.target.value)}
                        >
                          {embudo.etapas.map((s) => (
                            <option key={s.id} value={s.id}>
                              {s.nombre}
                            </option>
                          ))}
                        </select>
                      </article>
                    ))}
                    {porEtapa(stage).length === 0 && <p className="crm-vacio">—</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      <AsistenteCrm
        idioma={idioma}
        abierto={asistenteAbierto}
        onCerrar={() => setAsistenteAbierto(false)}
        onAplicado={sustituir}
      />

      {detalle && embudos && (
        <FichaOportunidad
          key={detalle.id}
          idioma={idioma}
          oportunidad={detalle}
          embudos={embudos}
          esDireccion={rol === 'DIRECTOR'}
          onCambio={(op) => sustituir([op])}
          onCerrar={() => setDetalle(null)}
        />
      )}
    </main>
  );
}

// --- El contacto y lo que la oportunidad trajo consigo -----------------------

/**
 * Lo que hace falta para trabajar el lead, en la propia tarjeta.
 *
 * Un embudo con noventa y siete oportunidades se usa llamando, y llamar
 * empezaba por abrir la ficha para copiar el telefono. Aqui el telefono ES el
 * enlace: se pulsa y marca. Lo mismo el correo.
 *
 * Las etiquetas son la lectura que el bot de WhatsApp ya hizo del contacto
 * —idioma, urgencia, si pidio que le dejaran en paz— y decidir a quien llamar
 * primero sin verlas es decidir a ciegas.
 */
function Contacto({ op, idioma }: { op: CrmOpportunity; idioma: Idioma }) {
  const quien = [op.contactoNombre, op.contactoEmpresa].filter(Boolean).join(' · ');
  const externos = Object.entries(op.datosExternos).filter(([, v]) => v.trim() !== '');
  const nada =
    quien === '' &&
    !op.contactoTelefono &&
    !op.contactoEmail &&
    op.etiquetas.length === 0 &&
    !op.origen &&
    !op.ownerOriginal &&
    externos.length === 0;
  if (nada) return null;

  return (
    <div className="crm-op-contacto">
      {quien !== '' && <p className="crm-op-quien">{quien}</p>}

      {(op.contactoTelefono || op.contactoEmail) && (
        <p className="crm-op-vias">
          {op.contactoTelefono && (
            <a href={`tel:${op.contactoTelefono}`} className="enlace">
              {op.contactoTelefono}
            </a>
          )}
          {op.contactoEmail && (
            <a href={`mailto:${op.contactoEmail}`} className="enlace">
              {op.contactoEmail}
            </a>
          )}
        </p>
      )}

      {op.etiquetas.length > 0 && (
        <p className="crm-op-etiquetas">
          {op.etiquetas.map((e) => (
            <span key={e} className="chip-tipo">
              {e}
            </span>
          ))}
        </p>
      )}

      {op.origen && (
        <p className="crm-op-origen">
          {t(idioma, 'crm.origen')}: {op.origen}
        </p>
      )}

      {op.ownerOriginal && (
        <p className="crm-op-origen">
          {t(idioma, 'crm.responsableOriginal')}: {op.ownerOriginal}
        </p>
      )}

      {/*
        La cualificacion heredada va plegada. Son hasta catorce campos y en
        abierto empujarian fuera de la vista la oportunidad siguiente; pero
        tirarlos seria perder lo unico que se sabe de un lead que nadie ha
        vuelto a tocar en dos meses.
      */}
      {externos.length > 0 && (
        <details className="crm-op-externos">
          <summary>{t(idioma, 'crm.datosExternos')}</summary>
          <dl>
            {externos.map(([clave, valor]) => (
              <div key={clave}>
                <dt>{clave}</dt>
                <dd>{valor}</dd>
              </div>
            ))}
          </dl>
        </details>
      )}
    </div>
  );
}
